import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {IonContent, MenuController, ModalController} from '@ionic/angular';
import {Subscription} from 'rxjs';
import {NavigationEnd, NavigationExtras, NavigationStart, Router} from '@angular/router';
import {Operatore} from './_models/Operatore';
import {AuthenticationService} from './_services/authentication.service';
import {OperatoriService} from './_services/operatori.service';
import {OrganizzazioniService} from './_services/organizzazioni.service';
import {LocalService} from './_services/local.service';
import {EventiService} from './_services/eventi.service';
import {RisorseService} from './_services/risorse.service';
import {constants} from './_helpers/constants';
import {OrganizzazioniCreateComponent} from './organizzazioni/organizzazioni-create/organizzazioni-create.component';
import {OrdiniGroupColumnsComponent} from './_components/ordini-group-columns/ordini-group-columns.component';
import {OrdiniShowColumnsComponent} from './_components/ordini-show-columns/ordini-show-columns.component';
import {FiltroOrdineComponent} from './_components/filtro-ordine/filtro-ordine.component';
import {LavorazioniService} from './_services/lavorazioni.service';
import {MovimentiService} from './_services/movimenti.service';
import {QualitaService} from './_services/qualita.service';
import {OrdinispedizioniService} from './_services/ordinispedizioni.service';
import {BilanceService} from './_services/bilance.service';
import {SpedizioniService} from "./_services/spedizioni.service";
import {QualitaSchedeService} from "./_services/qualitaschede.service";
import {LicenzeService} from "./_services/licenze.service";
import {MenuAccessService} from './_services/menu-access.service';
import {AvanzamentoService} from "./_services/avanzamento.service";
import {OrdiniSpedizioneBoxService} from "./_services/ordinispedizionibox.service";
import {PianificazioneRealtimeService} from './_services/pianificazione-realtime.service';
import {SaleOrdiniService} from './_services/sale-ordini.service';
import {environment} from '../environments/environment';
import {SaveMessage} from './_helpers/save-message';
import {registerNotificationPreview} from './_helpers/notification-preview';
import {InactivityLogoutService} from './_services/inactivity-logout.service';
import {resolveInactivityTimeout} from './_helpers/inactivity-timeout';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  subscription: Subscription;
  isStandalone = false;
  isAuthenticated = false;
  constants = constants;
  menuVisible = false;
  themeToggle = false;
  loading = false;
  private realtimeConnectionsStarted = false;
  private lastCurrentUser: Operatore | null = null;
  private ariaHiddenFocusObserver?: MutationObserver;

  // Posizione di scorrimento del menù laterale, tenuta in memoria per la sola sessione (nessuna
  // persistenza richiesta tra login diversi). Serve perché il browser la azzera da sé in due
  // momenti: ion-menu è `display: none` da chiusa (:host in menu.md.css) e riparte da cima appena
  // torna visibile, e l'*ngIf su `loading` distrugge e ricrea l'intera ion-content a ogni ricarica
  // dei moduli (login, cambio organizzazione, refresh del token).
  private menuScrollElement?: HTMLElement;
  private menuScrollTop = 0;

  constructor(
    public authService: AuthenticationService,
    public licenseService: LicenzeService,
    // Visibilità delle voci di menù: dizionario + binding globali + rifinitura dell'organizzazione.
    // Nome diverso da `menu` (MenuController), già usato più sotto.
    public menuAccess: MenuAccessService,
    public organizzazioniService: OrganizzazioniService,
    private operatoriService: OperatoriService,
    private modalCtrl: ModalController,
    private menu: MenuController,
    private router: Router,
    private avanzamentoService: AvanzamentoService,
    private localService: LocalService,
    private eventiService: EventiService,
    private risorseService: RisorseService,
    private lavorazioniService: LavorazioniService,
    private movimentiService: MovimentiService,
    private qualitaSchedeService: QualitaSchedeService,
    private qualitaService: QualitaService,
    private ordiniSpedizioniService: OrdinispedizioniService,
    private ordiniSpedizioniBoxService: OrdiniSpedizioneBoxService,
    private spedizioniService: SpedizioniService,
    private bilanceService: BilanceService,
    private pianificazioneRealtimeService: PianificazioneRealtimeService,
    private saleOrdiniService: SaleOrdiniService,
    private inactivityLogoutService: InactivityLogoutService,
    private saveMessage: SaveMessage) {}

  // Setter invece di lettura diretta: con l'*ngIf la ion-content va e viene, e questo è l'unico
  // punto che intercetta ogni nuovo aggancio per riapplicare subito la posizione salvata.
  @ViewChild('menuScrollContent') set menuScrollContent(content: IonContent | undefined) {
    this.menuScrollElement = undefined;
    if (!content) {
      return;
    }
    // try/catch oltre al .catch(): se il web component sottostante non è ancora inizializzato,
    // getScrollElement() solleva in modo SINCRONO e la promise non arriva mai. Non essendo un
    // errore gestibile, senza questo guard risalirebbe fino all'aggiornamento delle query di
    // Angular, rompendo il ciclo di change detection dell'intera applicazione.
    try {
      void content.getScrollElement()
        .then(element => {
          this.menuScrollElement = element;
          this.restoreMenuScroll();
        })
        .catch(() => this.menuScrollElement = undefined);
    } catch {
      this.menuScrollElement = undefined;
    }
  }

  ngOnInit() {
    /* 
    Permette di utilizzare comandi come:
    window.archiConnectDebug?.previewNotifications()
    window.archiConnectDebug?.previewNotificationColors()
    window.archiConnectDebug?.previewNotificationPopup()
    window.archiConnectDebug?.clearNotifications()
    */
    if (!environment.production) {
      registerNotificationPreview(this.saveMessage);
    }

    // Sessione ripristinata da localStorage dopo che il browser è rimasto chiuso oltre il timeout di
    // inattività: va chiusa QUI, prima di tutto il resto. ⚠️ L'ordine conta: se il rinnovo partisse
    // per primo, ruoterebbe il refresh token di una sessione già scaduta e la sua risposta
    // (currentUser con un token nuovo) rianimerebbe la sessione appena chiusa. Chiusa per prima,
    // currentUser è già azzerato e updateRefreshToken() non ha nulla da rinnovare.
    this.inactivityLogoutService.expireRestoredSession(this.currentInactivityTimeout());
    void this.authService.updateRefreshToken()
      .catch(err => console.error('updateRefreshToken error', err));
    this.lastCurrentUser = this.cloneUserSnapshot(this.authService.currentUserValue);
    this.isAuthenticated = !!this.authService.currentUserValue?.jwtToken;
    this.subscription = this.authService.currentUser.subscribe((_user: Operatore) => {
      const previousUser = this.lastCurrentUser;
      void this.handleCurrentUserChange(previousUser, _user);
    });
    this.subscription.add(this.organizzazioniService.showMenu.subscribe((value: boolean) => this.menuVisible = value));
    this.subscription.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationStart || event instanceof NavigationEnd) {
        this.blurActiveElement();
      }
    }));
    // Scadenza sessione per operatore (issue #3301, FR-006): il cambio organizzazione ri-emette le
    // impostazioni di membership (setCurrentOrganizzazioneValueById) e il nuovo valore deve
    // applicarsi senza ricaricare l'applicazione.
    // ⚠️ La subscription emette SUBITO all'avvio (BehaviorSubject idratato da localStorage), anche
    // a sessione assente: è il guard di applyInactivityTimeout() a renderla innocua sulla pagina
    // di login. ⚠️ Nessun restart incondizionato di InactivityLogoutService: start() è idempotente
    // solo a parità di valore e questo canale ri-emette a ogni salvataggio dei modali di colonne;
    // un restart a ogni emissione azzererebbe il conteggio e renderebbe il logout inefficace.
    this.subscription.add(this.organizzazioniService.currentAccountSettings
      .subscribe(() => this.applyInactivityTimeout()));
    this.observeAriaHiddenFocusLeaks();
    this.subscription.add(this.organizzazioniService.currentOrganizzazione
      .subscribe(() => {
        this.isStandalone = this.organizzazioniService.isStandalone();
        void this.refreshModule();
      }));
    // Use matchMedia to check the user preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    // Initialize the dark theme based on the initial
    // value of the prefers-color-scheme media query
    this.initializeDarkTheme(prefersDark.matches);
    // Listen for changes to the prefers-color-scheme media query
    prefersDark.addEventListener('change', (mediaQuery) => {
      // Aggiorna solo se l'utente non ha impostato una preferenza manuale
      if (localStorage.getItem('theme-dark') === null) {
        this.initializeDarkTheme(mediaQuery.matches);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.ariaHiddenFocusObserver?.disconnect();
  }

  // Rete di sicurezza generale per il warning "Blocked aria-hidden on an element because its
  // descendant retained focus": invece di inseguire ogni singolo trigger che può nascondere una
  // ion-page (navigazione, chiusura menu, cambio tab...), osserva direttamente la condizione che
  // genera il warning. Ogni volta che un elemento diventa aria-hidden="true", se contiene

  async refreshModule(): Promise<void> {
    if (this.authService.currentUserValue && this.authService.currentUserValue.jwtToken) {
      // `loading` fa da interruttore all'*ngIf della ion-content del menù: la posizione va letta
      // adesso, perché un istante dopo il contenitore non esiste più.
      this.saveMenuScroll();
      this.loading = true;
      try {
        // ensureModuliOfOrgLoadedAsync (non refreshModuliOfOrgAsync): qui serve solo garantire che i
        // dati dell'organizzazione corrente siano caricati, non forzarne uno nuovo. La deduplica con
        // gli altri chiamanti (es. account-login.component dopo il login) vive nel servizio, l'unico
        // punto che li vede tutti.
        await this.licenseService.ensureModuliOfOrgLoadedAsync(this.organizzazioniService.organizzazioneId);
      } finally {
        this.loading = false;
      }
    }
  }

  // Check/uncheck the toggle and update the theme based on isDark
  initializeDarkTheme(isDark: boolean) {
    const savedTheme = localStorage.getItem('theme-dark');
    if (savedTheme !== null) {
      this.themeToggle = savedTheme === 'true';
    } else {
      this.themeToggle = isDark;
    }
    this.toggleDarkTheme(this.themeToggle);
  }

  // Listen for the toggle check/uncheck to toggle the dark theme
  toggleChange(ev: Event) {
    const checked = (ev as CustomEvent<{ checked?: boolean }>).detail?.checked ?? false;
    this.toggleDarkTheme(checked);
    localStorage.setItem('theme-dark', checked.toString());
  }

  // Add or remove the "dark" class on the document body
  toggleDarkTheme(shouldAdd: boolean) {
    document.body.classList.toggle('dark', shouldAdd);
    document.documentElement.classList.toggle('dark', shouldAdd);
  }

  setOrganizzazione = (orgId: number) => {
    this.operatoriService.setOrganizzazioneAsync(orgId)
      .then(() => this.setActiveOrganizzazione(orgId));
  };

  setActiveOrganizzazione = (orgId: number) => {
    return this.switchActiveOrganizzazione(orgId, true);
  };

  async openModal() {
    const modal = await this.modalCtrl.create({
      component: OrganizzazioniCreateComponent,
      backdropDismiss: false,
      cssClass: 'organizzazione-create-modal'
    });
    await modal.present();
  }

  async openOrdiniGroupColumnsModal() {
    const modal = await this.modalCtrl.create({
      component: OrdiniGroupColumnsComponent,
      backdropDismiss: false
    });
    await modal.present();
    const {role} = await modal.onWillDismiss();
    if (role === 'confirm') {
      //this.fields.push(data);
      this.localService.handleRefresh.next(1);
    }
  }

  async openOrdiniShowColumnsModal() {
    const modal = await this.modalCtrl.create({
      component: OrdiniShowColumnsComponent,
      backdropDismiss: false
    });
    await modal.present();
    const {role} = await modal.onWillDismiss();
    if (role === 'confirm') {
      //this.fields.push(data);
      this.localService.handleRefresh.next(1);
    }
  }

  async openTipoFiltroOrdineModal() {
    const modal = await this.modalCtrl.create({
      component: FiltroOrdineComponent,
      backdropDismiss: false,
      cssClass: 'custom-modal'
    });
    await modal.present();
    const {role} = await modal.onWillDismiss();
    if (role === 'confirm') {
      //this.fields.push(data);
      this.localService.handleRefresh.next(1);
    }
  }

  async navigateAndClose(route: string, navigationExtras?: NavigationExtras): Promise<boolean> {
    this.blurActiveElement();
    await this.menu.close();
    return navigationExtras
      ? this.router.navigate([route], navigationExtras)
      : this.router.navigate([route]);
  }

  logout = () => {
    this.blurActiveElement();
    // Naviga PRIMA di distruggere la sessione: se la rotta corrente ha un CanDeactivate guard
    // (es. linea bilancia attiva su spedizione-manuale), l'utente è ancora autenticato quando
    // il guard esegue la sua logica, e annullare la navigazione annulla anche il logout.
    // Forza la navigazione anche dalla dashboard: con la configurazione predefinita Angular
    // ignora la navigazione verso la stessa URL e restituisce false, impedendo il logout.
    void this.navigateAndClose('/', {onSameUrlNavigation: 'reload'}).then((navigated) => {
      // Con revoca del refresh token: logout() da solo lo lascerebbe valido per 7 giorni.
      if (navigated) void this.authService.logoutAndRevoke();
    });
  };

  onMenuWillOpen() {
    // 1) evita aria-hidden su container con focus
    this.blurActiveElement();

    // 2) riporta il menù dove era stato lasciato, prima che venga disegnato
    this.restoreMenuScroll();

    // 3) sposta focus dentro al menu (accessibilità corretta)
    setTimeout(() => {
      // prima prova il primo item del menu (Dashboard)
      const first =
        document.getElementById('firstMenuItem') as HTMLElement | null;

      if (first) {
        // preventScroll: senza, il focus sul PRIMO item lo porta in vista e riporta comunque il
        // menù in cima, vanificando il ripristino qui sopra.
        first.focus({preventScroll: true});
        this.restoreMenuScroll();
        return;
      }

      // fallback: primo ion-item/button dentro al menu
      const anyFocusable =
        document.querySelector('ion-menu ion-item, ion-menu button, ion-menu [tabindex]') as HTMLElement | null;

      anyFocusable?.focus({preventScroll: true});
      this.restoreMenuScroll();
    }, 0);
  }

  onMenuWillClose() {
    // Prima della chiusura, non dopo: a menù chiuso ion-menu è display:none e il browser ha già
    // azzerato lo scroll del contenitore, quindi non ci sarebbe più niente da salvare.
    this.saveMenuScroll();
  }

  onMenuDidClose() {
    // La chiusura via backdrop può lasciare temporaneamente il focus sul backdrop
    // (che è aria-hidden). Ripetiamo il ripristino dopo l'animazione come fallback.
    setTimeout(() => {
      if (this.focusMenuButton()) {
        return;
      }

      // fallback: qualcosa di sensato nel contenuto principale
      (document.querySelector('ion-router-outlet button, ion-router-outlet [tabindex]') as HTMLElement | null)?.focus();
    }, 0);
  }

  onMenuPointerDown(event: PointerEvent): void {
    const backdropPressed = event.composedPath().some((element) =>
      element instanceof HTMLElement && element.matches('ion-backdrop.menu-backdrop'));

    if (!backdropPressed) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.blurActiveElement();
    void this.menu.close();
  }

  // l'elemento attualmente a fuoco lo blurra immediatamente.
  private observeAriaHiddenFocusLeaks() {
    this.ariaHiddenFocusObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const target = mutation.target as HTMLElement;
        // Limitato alle ion-page (il caso osservato finora: pagine di ion-router-outlet nascoste
        // in transizione): evita di reagire a un aria-hidden qualsiasi applicato altrove in app
        // per motivi indipendenti, dove un blur "a sorpresa" potrebbe innescare handler (ionBlur)
        // con effetti collaterali (validazioni, chiamate API) su un campo non più visibile.
        if (!target.classList?.contains('ion-page') || target.getAttribute?.('aria-hidden') !== 'true') {
          continue;
        }
        const active = document.activeElement as HTMLElement | null;
        if (active && target.contains(active)) {
          this.blurActiveElement();
        }
      }
    });
    this.ariaHiddenFocusObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true
    });
  }

  private saveMenuScroll(): void {
    if (this.menuScrollElement) {
      this.menuScrollTop = this.menuScrollElement.scrollTop;
    }
  }

  private restoreMenuScroll(): void {
    // Solo se c'è davvero una posizione da ripristinare: scrivere 0 su un contenitore non ancora
    // misurato non serve, e lasciarlo stare evita di interferire con la prima apertura.
    if (this.menuScrollElement && this.menuScrollTop > 0) {
      this.menuScrollElement.scrollTop = this.menuScrollTop;
    }
  }

  private blurActiveElement() {
    const active = document.activeElement as HTMLElement | null;
    const nestedActive = active?.shadowRoot?.activeElement as HTMLElement | null;

    nestedActive?.blur();
    active?.blur();
  }

  private focusMenuButton(): boolean {
    const menuButton = document.getElementById('menuButton') as HTMLElement | null;
    const nativeButton = menuButton?.shadowRoot?.querySelector('button') as HTMLButtonElement | null;

    if (nativeButton) {
      nativeButton.focus();
      return true;
    }

    if (menuButton) {
      menuButton.focus();
      return true;
    }

    return false;
  }

  private shouldRefreshModules(previousUser: Operatore | null, currentUser: Operatore): boolean {
    if (!currentUser?.jwtToken) {
      return false;
    }

    if (!previousUser) {
      return true;
    }

    return previousUser.id !== currentUser.id
      || previousUser.jwtToken !== currentUser.jwtToken
      || previousUser.organizzazioneId !== currentUser.organizzazioneId;
  }

  private cloneUserSnapshot(user: Operatore | null): Operatore | null {
    if (!user) {
      return null;
    }

    return {
      ...user,
      roles: [...(user.roles ?? [])]
    };
  }

  private startRealtimeConnections() {
    if (this.realtimeConnectionsStarted || !this.authService.currentUserValue?.jwtToken) {
      return;
    }

    this.realtimeConnectionsStarted = true;
    this.startConnectionWithLog('Avanzamento', () => this.avanzamentoService.startConnection());
    this.startConnectionWithLog('Local', () => this.localService.startConnection());
    this.startConnectionWithLog('Eventi', () => this.eventiService.startConnection());
    this.startConnectionWithLog('Risorse', () => this.risorseService.startConnection());
    this.startConnectionWithLog('Lavorazioni', () => this.lavorazioniService.startConnection());
    this.startConnectionWithLog('Movimenti', () => this.movimentiService.startConnection());
    this.startConnectionWithLog('QualitaSchede', () => this.qualitaSchedeService.startConnection());
    this.startConnectionWithLog('Qualita', () => this.qualitaService.startConnection());
    this.startConnectionWithLog('OrdiniSpedizioni', () => this.ordiniSpedizioniService.startConnection());
    this.startConnectionWithLog('Spedizioni', () => this.spedizioniService.startConnection());
    this.startConnectionWithLog('OrdiniSpedizioniBox', () => this.ordiniSpedizioniBoxService.startConnection());
    this.startConnectionWithLog('Bilance', () => this.bilanceService.startConnection());
    this.startConnectionWithLog('PianificazioneRealtime', () => this.pianificazioneRealtimeService.startConnection());
    this.startConnectionWithLog('SaleOrdini', () => this.saleOrdiniService.startConnection());
  }

  private startConnectionWithLog(connectionName: string, startConnection: () => Promise<unknown>): void {
    startConnection()
      .catch(err => console.error(`${connectionName} startConnection error`, err));
  }

  private stopRealtimeConnections(): void {
    if (!this.realtimeConnectionsStarted) {
      return;
    }

    this.realtimeConnectionsStarted = false;
    this.avanzamentoService.stopConnection();
    this.localService.stopConnection();
    this.eventiService.stopConnection();
    this.risorseService.stopConnection();
    this.lavorazioniService.stopConnection();
    this.movimentiService.stopConnection();
    this.qualitaSchedeService.stopConnection();
    this.qualitaService.stopConnection();
    this.ordiniSpedizioniService.stopConnection();
    this.spedizioniService.stopConnection();
    this.ordiniSpedizioniBoxService.stopConnection();
    this.bilanceService.stopConnection();
    this.pianificazioneRealtimeService.stopConnection();
    this.saleOrdiniService.stopConnection();
  }

  private async handleCurrentUserChange(previousUser: Operatore | null, user: Operatore | null): Promise<void> {
    this.isAuthenticated = !!user?.jwtToken;

    if (user) {
      const aligned = await this.alignCurrentOrganizationWithUser(user);
      this.startRealtimeConnections();
      // Logout automatico per inattività (issue #3203, override per operatore issue #3301):
      // avvia il tracking col valore risolto. Idempotente: le ri-emissioni da refresh token non
      // azzerano il conteggio.
      this.applyInactivityTimeout();
      if (!aligned) {
        this.refreshCurrentOrganizationState();
      }
      if (this.shouldRefreshModules(previousUser, user)) {
        await this.refreshModule();
      }
      this.lastCurrentUser = this.cloneUserSnapshot(user);
      return;
    }

    this.loading = false;
    this.lastCurrentUser = null;
    // Logout: chiude le connessioni real-time e azzera il latch, così al prossimo login
    // vengono ricreate da zero (senza dover ricaricare la pagina).
    this.stopRealtimeConnections();
    // Logout automatico per inattività (issue #3203): ferma il tracking sulla fine sessione.
    this.inactivityLogoutService.stop();
    // Un login successivo (anche per la stessa organizzazione, senza ricaricare la pagina) deve
    // rivedere moduli/binding aggiornati, non quelli della sessione appena chiusa.
    this.licenseService.resetSessionCache();
  }

  // Scadenza sessione per operatore (issue #3301, FR-002/FR-003/FR-004/FR-006/FR-033).
  // Unico punto che arma il timer di inattività: legge i due canali (globale dall'AuthApi,
  // override dalla membership) e li risolve con resolveInactivityTimeout().
  private applyInactivityTimeout(): void {
    // ⚠️ Guard di sessione (FR-033), PRIMA della lettura delle impostazioni:
    // currentAccountSettingsSubject è un BehaviorSubject idratato da localStorage e logout() non
    // rimuove la chiave 'currentOrgSettings'. Senza questo guard il timer si armerebbe sulla
    // pagina di login e comparirebbe l'alert "Sessione in scadenza" a sessione assente.
    if (!this.authService.currentUserValue?.jwtToken) {
      this.inactivityLogoutService.stop();
      return;
    }

    this.inactivityLogoutService.start(this.currentInactivityTimeout());
  }

  // Timeout di inattività effettivo: override della membership se presente (0 = disattivato per
  // questo operatore), altrimenti il globale dell'AuthApi. Letto da due BehaviorSubject idratati da
  // localStorage, quindi disponibile già in ngOnInit, prima di qualunque chiamata di rete.
  private currentInactivityTimeout(): number {
    const globale = this.authService.currentUserValue?.inactivityTimeoutMinutes;
    const override = this.organizzazioniService.currentAccountSettingsValue?.inactivityTimeoutMinutes;
    return resolveInactivityTimeout(override, globale);
  }

  private async alignCurrentOrganizationWithUser(user: Operatore | null): Promise<boolean> {
    const userOrgId = user?.organizzazioneId ?? 0;
    if (!user?.jwtToken || !userOrgId) {
      return false;
    }

    const currentOrgId = this.organizzazioniService.currentOrganizzazioneValue?.id ?? 0;
    const hasCurrentSettings = !!this.organizzazioniService.currentAccountSettingsValue;
    if (currentOrgId === userOrgId && hasCurrentSettings) {
      return false;
    }

    await this.switchActiveOrganizzazione(userOrgId, currentOrgId !== userOrgId);
    return true;
  }

  private async switchActiveOrganizzazione(orgId: number, resetOperationalState: boolean): Promise<void> {
    await this.organizzazioniService.setCurrentOrganizzazioneValueById(orgId);
    if (resetOperationalState) {
      this.resetOperationalBrowserState();
    }
    this.refreshCurrentOrganizationState();
  }

  private resetOperationalBrowserState(): void {
    this.localService.resetOrdineGroup(false, false);
    this.risorseService.resetRisorsa();
    this.lavorazioniService.resetLavorazione();
    this.ordiniSpedizioniService.resetOrdSpedGroup(false, false);
    this.bilanceService.resetBilancia();
    this.localService.searchText.next('');
  }

  private refreshCurrentOrganizationState(): void {
    this.risorseService.setMyRisorse(this.organizzazioniService.organizzazioneId);
    this.lavorazioniService.setMyLavorazioni(this.organizzazioniService.organizzazioneId);
    this.localService.searchText.next('');
  }
}
